import { executeGraphqlRequest, readCachedGraphqlQuery } from '../apollo/graphql-request';
import { uploadFileDirectly } from '../lib/direct-upload';

export type GalleryStyle = 'COVER' | 'EDITORIAL' | 'COMPACT';
export type GalleryLayout = 'GRID' | 'MASONRY' | 'SLIDESHOW';
// ALL and WEB give web copies, which need Pro (gallery_downloads); ORIGINAL and OFF are free.
export type GalleryDownloads = 'ALL' | 'WEB' | 'ORIGINAL' | 'OFF';
// Photo spacing (a multiplier of the design's gap) and the grid's photo shape; paid with Pro
// (gallery_look) except the free values DESIGN and SQUARE.
export type GallerySpacing = 'DESIGN' | 'SMALLER' | 'LARGER' | 'NONE';
export type GalleryPhotoShape = 'SQUARE' | 'ORIGINAL' | 'PORTRAIT' | 'LANDSCAPE';
export type GalleryAutoDelete = 'OFF' | 'NOW' | 'AFTER_30_DAYS' | 'AFTER_90_DAYS';
// The Gallery language of the guest page's own words; null speaks the site's language.
export type GalleryLanguage = 'uk' | 'en' | 'ru';
export type GalleryItemKind = 'IMAGE' | 'VIDEO';
export type GalleryItemState = 'PROCESSING' | 'READY' | 'FAILED';

export const GALLERY_STYLES: GalleryStyle[] = ['COVER', 'EDITORIAL', 'COMPACT'];
export const GALLERY_LAYOUTS: GalleryLayout[] = ['GRID', 'MASONRY', 'SLIDESHOW'];
export const GALLERY_SPACINGS: GallerySpacing[] = ['DESIGN', 'SMALLER', 'LARGER', 'NONE'];
export const GALLERY_PHOTO_SHAPES: GalleryPhotoShape[] = ['SQUARE', 'ORIGINAL', 'PORTRAIT', 'LANDSCAPE'];
export const GALLERY_DOWNLOADS: GalleryDownloads[] = ['ALL', 'WEB', 'ORIGINAL', 'OFF'];
export const FREE_GALLERY_DOWNLOADS: GalleryDownloads[] = ['ORIGINAL', 'OFF'];
export const GALLERY_LANGUAGES: GalleryLanguage[] = ['uk', 'en', 'ru'];
export const GALLERY_AUTO_DELETE: GalleryAutoDelete[] = [
  'OFF',
  'NOW',
  'AFTER_30_DAYS',
  'AFTER_90_DAYS',
];

export const GALLERY_PROVIDER_ONLY_ERROR_CODE = 'GALLERY_PROVIDER_ONLY';

export interface GalleryCounters {
  views: number;
  downloads: number;
  zips: number;
  favorites: number;
}

export interface GalleryItem {
  uuid: string;
  sectionUuid: string;
  kind: GalleryItemKind;
  state: GalleryItemState;
  position: number;
  width?: number | null;
  height?: number | null;
  thumb400Url?: string | null;
  thumb800Url?: string | null;
  webUrl?: string | null;
  posterUrl?: string | null;
  // A Gallery guest hid it for every guest (Hidden photo); guests no longer see it until restored.
  hidden?: boolean;
  media: { uuid: string; fileName: string };
}

// Which paid gallery settings the Account's Plan lets the Provider turn on or change
// (Gallery.planFeatures); a setting already in use keeps working and can be switched off.
export interface GalleryPlanFeatures {
  watermark: boolean;
  noMatchaMark: boolean;
  expiry: boolean;
  look: boolean;
  hiddenPhotos: boolean;
  // Downloads with web copies (ALL or WEB); originals only and off are free.
  downloads: boolean;
  // A limit on the Gallery selection; no limit is free.
  selectionLimit: boolean;
}

// The gallery's cover: a picked photo item or a JPG/PNG uploaded apart from the items.
export interface GalleryCover {
  uuid: string;
  uploaded: boolean;
  itemUuid?: string | null;
  state: GalleryItemState;
  thumb800Url?: string | null;
  webUrl?: string | null;
}

// Photos whose watermarked copy is made with the current Watermark; null while the switch is off.
export interface GalleryWatermarkProgress {
  ready: number;
  total: number;
  failed: number;
}

export interface GallerySection {
  uuid: string;
  name: string;
  position: number;
  items: GalleryItem[];
}

export interface GalleryComment {
  uuid: string;
  itemUuid: string;
  author?: string | null;
  text: string;
  createdAt: string;
}

export interface GallerySelection {
  count: number;
  itemUuids: string[];
  fileNames: string[];
  sentAt?: string | null;
  locked: boolean;
  items: GalleryItem[];
}

export interface Gallery {
  uuid: string;
  profileUuid: string;
  title: string;
  shootDate?: string | null;
  coverItemUuid?: string | null;
  hasNewSelection: boolean;
  itemCount: number;
  style: GalleryStyle;
  layout: GalleryLayout;
  spacing?: GallerySpacing;
  photoShape?: GalleryPhotoShape;
  cover?: GalleryCover | null;
  downloads: GalleryDownloads;
  language?: GalleryLanguage | null;
  // Guest hints: shown once per device after the password; on unless the Provider switched them off.
  guestHints?: boolean;
  // Hidden photos: guests may hide photos for everyone (paid with Pro, gallery_hidden_photos).
  allowHiding?: boolean;
  // Watermark: guests get web-size files with the Account's Watermark (paid with Pro, gallery_watermark).
  watermark?: boolean;
  watermarkProgress?: GalleryWatermarkProgress | null;
  planFeatures?: GalleryPlanFeatures;
  autoDelete: GalleryAutoDelete;
  expiresAt?: string | null;
  selectionLimit?: number | null;
  passwordEnabled: boolean;
  password?: string | null;
  link?: string | null;
  counters: GalleryCounters;
  sections: GallerySection[];
  selection: GallerySelection;
  comments: GalleryComment[];
}

// A list card and its banner: only the cover item is read (and signed), not every item.
export interface GallerySummary {
  uuid: string;
  title: string;
  shootDate?: string | null;
  coverItemUuid?: string | null;
  cover?: Pick<GalleryItem, 'uuid' | 'thumb800Url'> | null;
  hasNewSelection: boolean;
  itemCount: number;
  expiresAt?: string | null;
  counters: GalleryCounters;
  selection: Pick<GallerySelection, 'count' | 'sentAt' | 'locked'>;
}

// The answer of markGallerySelectionSeen; it updates the cached Gallery by uuid.
export interface GallerySelectionSeen {
  uuid: string;
  hasNewSelection: boolean;
  selectionSeenAt?: string | null;
}

export interface UpdateGalleryInput {
  title?: string;
  shootDate?: string | null;
  style?: GalleryStyle;
  layout?: GalleryLayout;
  spacing?: GallerySpacing;
  photoShape?: GalleryPhotoShape;
  downloads?: GalleryDownloads;
  language?: GalleryLanguage | null;
  guestHints?: boolean;
  allowHiding?: boolean;
  watermark?: boolean;
  autoDelete?: GalleryAutoDelete;
  expiresAt?: string | null;
  selectionLimit?: number | null;
  passwordEnabled?: boolean;
  // 4-64 characters; an empty string switches the password off.
  password?: string;
}

export type WatermarkKind = 'TEXT' | 'LOGO';
export type WatermarkPosition = 'CENTER' | 'BOTTOM_RIGHT' | 'TILED';
export type WatermarkStrength = 'LIGHT' | 'MEDIUM' | 'STRONG';

export const WATERMARK_KINDS: WatermarkKind[] = ['TEXT', 'LOGO'];
export const WATERMARK_POSITIONS: WatermarkPosition[] = ['CENTER', 'BOTTOM_RIGHT', 'TILED'];
export const WATERMARK_STRENGTHS: WatermarkStrength[] = ['LIGHT', 'MEDIUM', 'STRONG'];

// The Account's Watermark template (galleryWatermark), one for all its galleries.
export interface GalleryWatermark {
  kind: WatermarkKind;
  // Null draws the gallery's Profile name.
  text?: string | null;
  position: WatermarkPosition;
  strength: WatermarkStrength;
  hasLogo: boolean;
  logoFileName?: string | null;
  // Signed for an hour.
  logoUrl?: string | null;
  version: number;
  // The Plan includes gallery_watermark; without it the template shows but cannot change.
  canChange: boolean;
}

export interface UpdateGalleryWatermarkInput {
  kind?: WatermarkKind;
  text?: string | null;
  position?: WatermarkPosition;
  strength?: WatermarkStrength;
}

// The `complete` answer of the upload REST route.
export interface UploadedGalleryItem {
  uuid: string;
  galleryUuid: string;
  sectionUuid: string;
  kind: GalleryItemKind;
  state: GalleryItemState;
  fileName: string;
}

export interface UploadGalleryFileInput {
  galleryUuid: string;
  sectionUuid?: string | null;
  file: File;
  onProgress?: (share: number) => void;
}

const COUNTERS_FIELDS = `
    counters {
      views
      downloads
      zips
      favorites
    }`;

const ITEM_FIELDS = `
      uuid
      sectionUuid
      kind
      state
      position
      width
      height
      thumb400Url
      thumb800Url
      webUrl
      posterUrl
      hidden
      media {
        uuid
        fileName
      }`;

const GALLERY_FIELDS = `
    uuid
    profileUuid
    title
    shootDate
    coverItemUuid
    hasNewSelection
    itemCount
    style
    layout
    spacing
    photoShape
    cover {
      uuid
      uploaded
      itemUuid
      state
      thumb800Url
      webUrl
    }
    downloads
    language
    guestHints
    allowHiding
    watermark
    watermarkProgress {
      ready
      total
      failed
    }
    planFeatures {
      watermark
      noMatchaMark
      expiry
      look
      hiddenPhotos
      downloads
      selectionLimit
    }
    autoDelete
    expiresAt
    selectionLimit
    passwordEnabled
    password
    link${COUNTERS_FIELDS}
    sections {
      uuid
      name
      position
      items {${ITEM_FIELDS}
      }
    }
    selection {
      count
      itemUuids
      fileNames
      sentAt
      locked
      items {${ITEM_FIELDS}
      }
    }
    comments {
      uuid
      itemUuid
      author
      text
      createdAt
    }`;

const MY_GALLERIES_QUERY = `
  query MyGalleries($profileUuid: ID!) {
    myGalleries(profileUuid: $profileUuid) {
      uuid
      title
      shootDate
      coverItemUuid
      cover {
        uuid
        thumb800Url
      }
      hasNewSelection
      itemCount
      expiresAt${COUNTERS_FIELDS}
      selection {
        count
        sentAt
        locked
      }
    }
  }
`;

const GALLERY_QUERY = `
  query Gallery($uuid: ID!) {
    gallery(uuid: $uuid) {${GALLERY_FIELDS}
    }
  }
`;

function galleryMutation(name: string, field: string) {
  return `
  mutation ${name}($uuid: ID!) {
    ${field}(uuid: $uuid) {${GALLERY_FIELDS}
    }
  }
`;
}

const CREATE_GALLERY_MUTATION = `
  mutation CreateGallery($input: CreateGalleryInput!) {
    createGallery(input: $input) {${GALLERY_FIELDS}
    }
  }
`;

const UPDATE_GALLERY_MUTATION = `
  mutation UpdateGallery($uuid: ID!, $input: UpdateGalleryInput!) {
    updateGallery(uuid: $uuid, input: $input) {${GALLERY_FIELDS}
    }
  }
`;

const DELETE_GALLERY_MUTATION = `
  mutation DeleteGallery($uuid: ID!) {
    deleteGallery(uuid: $uuid)
  }
`;

const MARK_GALLERY_SELECTION_SEEN_MUTATION = `
  mutation MarkGallerySelectionSeen($uuid: ID!) {
    markGallerySelectionSeen(uuid: $uuid) {
      uuid
      hasNewSelection
      selectionSeenAt
    }
  }
`;

const CREATE_GALLERY_PREVIEW_TOKEN_MUTATION = `
  mutation CreateGalleryPreviewToken($uuid: ID!) {
    createGalleryPreviewToken(uuid: $uuid) {
      token
      expiresAt
    }
  }
`;

const SET_GALLERY_COVER_MUTATION = `
  mutation SetGalleryCover($galleryUuid: ID!, $itemUuid: ID) {
    setGalleryCover(galleryUuid: $galleryUuid, itemUuid: $itemUuid) {${GALLERY_FIELDS}
    }
  }
`;

const ADD_GALLERY_SECTION_MUTATION = `
  mutation AddGallerySection($galleryUuid: ID!, $name: String) {
    addGallerySection(galleryUuid: $galleryUuid, name: $name) {
      uuid
      name
      position
    }
  }
`;

const RENAME_GALLERY_SECTION_MUTATION = `
  mutation RenameGallerySection($uuid: ID!, $name: String!) {
    renameGallerySection(uuid: $uuid, name: $name) {
      uuid
      name
      position
    }
  }
`;

const DELETE_GALLERY_SECTION_MUTATION = `
  mutation DeleteGallerySection($uuid: ID!) {
    deleteGallerySection(uuid: $uuid)
  }
`;

const REORDER_GALLERY_ITEMS_MUTATION = `
  mutation ReorderGalleryItems($sectionUuid: ID!, $itemUuids: [ID!]!) {
    reorderGalleryItems(sectionUuid: $sectionUuid, itemUuids: $itemUuids) {
      uuid
      sectionUuid
      position
    }
  }
`;

const MOVE_GALLERY_ITEMS_MUTATION = `
  mutation MoveGalleryItems($itemUuids: [ID!]!, $sectionUuid: ID!) {
    moveGalleryItems(itemUuids: $itemUuids, sectionUuid: $sectionUuid) {
      uuid
      sectionUuid
      position
    }
  }
`;

const DELETE_GALLERY_ITEMS_MUTATION = `
  mutation DeleteGalleryItems($itemUuids: [ID!]!) {
    deleteGalleryItems(itemUuids: $itemUuids)
  }
`;

const RESTORE_GALLERY_ITEM_MUTATION = `
  mutation RestoreGalleryItem($uuid: ID!) {
    restoreGalleryItem(uuid: $uuid) {
      uuid
      hidden
    }
  }
`;

const WATERMARK_FIELDS = `
      kind
      text
      position
      strength
      hasLogo
      logoFileName
      logoUrl
      version
      canChange`;

const GALLERY_WATERMARK_QUERY = `
  query GalleryWatermark {
    galleryWatermark {${WATERMARK_FIELDS}
    }
  }
`;

const UPDATE_GALLERY_WATERMARK_MUTATION = `
  mutation UpdateGalleryWatermark($input: UpdateGalleryWatermarkInput!) {
    updateGalleryWatermark(input: $input) {${WATERMARK_FIELDS}
    }
  }
`;

const REMOVE_GALLERY_WATERMARK_LOGO_MUTATION = `
  mutation RemoveGalleryWatermarkLogo {
    removeGalleryWatermarkLogo {${WATERMARK_FIELDS}
    }
  }
`;

const REISSUE_GALLERY_LINK_MUTATION = galleryMutation('ReissueGalleryLink', 'reissueGalleryLink');
const NEW_GALLERY_PASSWORD_MUTATION = galleryMutation('NewGalleryPassword', 'newGalleryPassword');
const UNLOCK_GALLERY_SELECTION_MUTATION = galleryMutation(
  'UnlockGallerySelection',
  'unlockGallerySelection'
);

// Every document this module sends, for the contract test against the API schema.
export const GALLERY_DOCUMENTS = {
  MY_GALLERIES_QUERY,
  GALLERY_QUERY,
  CREATE_GALLERY_MUTATION,
  UPDATE_GALLERY_MUTATION,
  DELETE_GALLERY_MUTATION,
  SET_GALLERY_COVER_MUTATION,
  ADD_GALLERY_SECTION_MUTATION,
  RENAME_GALLERY_SECTION_MUTATION,
  DELETE_GALLERY_SECTION_MUTATION,
  REORDER_GALLERY_ITEMS_MUTATION,
  MOVE_GALLERY_ITEMS_MUTATION,
  DELETE_GALLERY_ITEMS_MUTATION,
  RESTORE_GALLERY_ITEM_MUTATION,
  REISSUE_GALLERY_LINK_MUTATION,
  NEW_GALLERY_PASSWORD_MUTATION,
  UNLOCK_GALLERY_SELECTION_MUTATION,
  MARK_GALLERY_SELECTION_SEEN_MUTATION,
  CREATE_GALLERY_PREVIEW_TOKEN_MUTATION,
  GALLERY_WATERMARK_QUERY,
  UPDATE_GALLERY_WATERMARK_MUTATION,
  REMOVE_GALLERY_WATERMARK_LOGO_MUTATION,
};

const GRAPHQL_URL = process.env.REACT_APP_API_GRAPHQL_URL ?? 'http://localhost:3000/graphql';

function resolveApiBaseUrl() {
  const configuredBaseUrl = process.env.REACT_APP_API_BASE_URL?.trim();
  if (configuredBaseUrl) {
    return configuredBaseUrl.replace(/\/+$/, '');
  }

  return GRAPHQL_URL.replace(/\/graphql\/?$/, '');
}

function request<TData>(
  query: string,
  variables: Record<string, unknown>,
  requestName: string,
  accessToken?: string
) {
  return executeGraphqlRequest<TData>(query, variables, { accessToken, requestName });
}

export async function fetchMyGalleries(
  profileUuid: string,
  accessToken?: string
): Promise<GallerySummary[]> {
  const data = await request<{ myGalleries: GallerySummary[] }>(
    MY_GALLERIES_QUERY,
    { profileUuid },
    'MyGalleries',
    accessToken
  );
  return data.myGalleries;
}

export function readCachedMyGalleries(profileUuid: string): GallerySummary[] | undefined {
  return readCachedGraphqlQuery<{ myGalleries: GallerySummary[] }>(MY_GALLERIES_QUERY, {
    profileUuid,
  })?.myGalleries;
}

export async function fetchGallery(uuid: string, accessToken?: string): Promise<Gallery> {
  const data = await request<{ gallery: Gallery }>(GALLERY_QUERY, { uuid }, 'Gallery', accessToken);
  return data.gallery;
}

export function readCachedGallery(uuid: string): Gallery | undefined {
  return readCachedGraphqlQuery<{ gallery: Gallery }>(GALLERY_QUERY, { uuid })?.gallery;
}

// Defaults are the server's: one section, password on, Cover + grid, no expiry.
export async function createGalleryRequest(
  profileUuid: string,
  accessToken?: string
): Promise<Gallery> {
  const data = await request<{ createGallery: Gallery }>(
    CREATE_GALLERY_MUTATION,
    { input: { profileUuid } },
    'CreateGallery',
    accessToken
  );
  return data.createGallery;
}

export async function updateGalleryRequest(
  uuid: string,
  input: UpdateGalleryInput,
  accessToken?: string
): Promise<Gallery> {
  const data = await request<{ updateGallery: Gallery }>(
    UPDATE_GALLERY_MUTATION,
    { uuid, input },
    'UpdateGallery',
    accessToken
  );
  return data.updateGallery;
}

export async function deleteGalleryRequest(uuid: string, accessToken?: string): Promise<boolean> {
  const data = await request<{ deleteGallery: boolean }>(
    DELETE_GALLERY_MUTATION,
    { uuid },
    'DeleteGallery',
    accessToken
  );
  return data.deleteGallery;
}

// Opening a gallery or closing its banner: the current sending is no longer new, on any device.
export async function markGallerySelectionSeenRequest(
  uuid: string,
  accessToken?: string
): Promise<GallerySelectionSeen> {
  const data = await request<{ markGallerySelectionSeen: GallerySelectionSeen }>(
    MARK_GALLERY_SELECTION_SEEN_MUTATION,
    { uuid },
    'MarkGallerySelectionSeen',
    accessToken
  );
  return data.markGallerySelectionSeen;
}

// The owner's preview of the guest page (Design view): a token of about an hour that opens the page
// without the password when given as `#preview=<token>` (galleryPreviewSrc).
export interface GalleryPreviewToken {
  token: string;
  expiresAt: string;
}

export async function createGalleryPreviewTokenRequest(
  uuid: string,
  accessToken?: string
): Promise<GalleryPreviewToken> {
  const data = await request<{ createGalleryPreviewToken: GalleryPreviewToken }>(
    CREATE_GALLERY_PREVIEW_TOKEN_MUTATION,
    { uuid },
    'CreateGalleryPreviewToken',
    accessToken
  );
  return data.createGalleryPreviewToken;
}

export async function setGalleryCoverRequest(
  galleryUuid: string,
  itemUuid: string | null,
  accessToken?: string
): Promise<Gallery> {
  const data = await request<{ setGalleryCover: Gallery }>(
    SET_GALLERY_COVER_MUTATION,
    { galleryUuid, itemUuid },
    'SetGalleryCover',
    accessToken
  );
  return data.setGalleryCover;
}

export async function addGallerySectionRequest(
  galleryUuid: string,
  name: string,
  accessToken?: string
): Promise<Pick<GallerySection, 'uuid' | 'name' | 'position'>> {
  const data = await request<{ addGallerySection: GallerySection }>(
    ADD_GALLERY_SECTION_MUTATION,
    { galleryUuid, name },
    'AddGallerySection',
    accessToken
  );
  return data.addGallerySection;
}

export async function renameGallerySectionRequest(
  uuid: string,
  name: string,
  accessToken?: string
): Promise<Pick<GallerySection, 'uuid' | 'name' | 'position'>> {
  const data = await request<{ renameGallerySection: GallerySection }>(
    RENAME_GALLERY_SECTION_MUTATION,
    { uuid, name },
    'RenameGallerySection',
    accessToken
  );
  return data.renameGallerySection;
}

export async function deleteGallerySectionRequest(
  uuid: string,
  accessToken?: string
): Promise<boolean> {
  const data = await request<{ deleteGallerySection: boolean }>(
    DELETE_GALLERY_SECTION_MUTATION,
    { uuid },
    'DeleteGallerySection',
    accessToken
  );
  return data.deleteGallerySection;
}

// `itemUuids` names every item of the section once, in the new order.
export async function reorderGalleryItemsRequest(
  sectionUuid: string,
  itemUuids: string[],
  accessToken?: string
): Promise<void> {
  await request(
    REORDER_GALLERY_ITEMS_MUTATION,
    { sectionUuid, itemUuids },
    'ReorderGalleryItems',
    accessToken
  );
}

export async function moveGalleryItemsRequest(
  itemUuids: string[],
  sectionUuid: string,
  accessToken?: string
): Promise<void> {
  await request(
    MOVE_GALLERY_ITEMS_MUTATION,
    { itemUuids, sectionUuid },
    'MoveGalleryItems',
    accessToken
  );
}

export async function deleteGalleryItemsRequest(
  itemUuids: string[],
  accessToken?: string
): Promise<void> {
  await request(DELETE_GALLERY_ITEMS_MUTATION, { itemUuids }, 'DeleteGalleryItems', accessToken);
}

// Brings a Hidden photo back for every guest.
export async function restoreGalleryItemRequest(uuid: string, accessToken?: string): Promise<void> {
  await request(RESTORE_GALLERY_ITEM_MUTATION, { uuid }, 'RestoreGalleryItem', accessToken);
}

export async function reissueGalleryLinkRequest(
  uuid: string,
  accessToken?: string
): Promise<Gallery> {
  const data = await request<{ reissueGalleryLink: Gallery }>(
    REISSUE_GALLERY_LINK_MUTATION,
    { uuid },
    'ReissueGalleryLink',
    accessToken
  );
  return data.reissueGalleryLink;
}

export async function newGalleryPasswordRequest(
  uuid: string,
  accessToken?: string
): Promise<Gallery> {
  const data = await request<{ newGalleryPassword: Gallery }>(
    NEW_GALLERY_PASSWORD_MUTATION,
    { uuid },
    'NewGalleryPassword',
    accessToken
  );
  return data.newGalleryPassword;
}

export async function unlockGallerySelectionRequest(
  uuid: string,
  accessToken?: string
): Promise<Gallery> {
  const data = await request<{ unlockGallerySelection: Gallery }>(
    UNLOCK_GALLERY_SELECTION_MUTATION,
    { uuid },
    'UnlockGallerySelection',
    accessToken
  );
  return data.unlockGallerySelection;
}

// REST, as for Service media: presigned POST into `pending/`, then `complete` makes the item
// (PROCESSING until its thumbnails are made).
export function uploadGalleryFileRequest(
  { galleryUuid, sectionUuid, file, onProgress }: UploadGalleryFileInput,
  accessToken?: string
): Promise<UploadedGalleryItem> {
  return uploadFileDirectly<UploadedGalleryItem>({
    accessToken,
    extraFields: sectionUuid ? { sectionUuid } : {},
    file,
    onUploadProgress: onProgress,
    routeUrl: `${resolveApiBaseUrl()}/galleries/${encodeURIComponent(galleryUuid)}/media`,
  });
}

// The answer of the cover `complete` route; the cover stays PROCESSING until its copies are made.
export interface UploadedGalleryCover {
  uuid: string;
  galleryUuid: string;
  state: GalleryItemState;
  fileName: string;
}

// A separate cover (JPG or PNG) through `galleries/:uuid/cover/upload-url` → S3 → `complete`; it
// replaces the picked or previously uploaded cover. Read the gallery again for its URLs.
export function uploadGalleryCoverRequest(
  galleryUuid: string,
  file: File,
  accessToken?: string
): Promise<UploadedGalleryCover> {
  return uploadFileDirectly<UploadedGalleryCover>({
    accessToken,
    file,
    routeUrl: `${resolveApiBaseUrl()}/galleries/${encodeURIComponent(galleryUuid)}/cover`,
  });
}

export async function fetchGalleryWatermark(accessToken?: string): Promise<GalleryWatermark> {
  const data = await request<{ galleryWatermark: GalleryWatermark }>(
    GALLERY_WATERMARK_QUERY,
    {},
    'GalleryWatermark',
    accessToken
  );
  return data.galleryWatermark;
}

export async function updateGalleryWatermarkRequest(
  input: UpdateGalleryWatermarkInput,
  accessToken?: string
): Promise<GalleryWatermark> {
  const data = await request<{ updateGalleryWatermark: GalleryWatermark }>(
    UPDATE_GALLERY_WATERMARK_MUTATION,
    { input },
    'UpdateGalleryWatermark',
    accessToken
  );
  return data.updateGalleryWatermark;
}

export async function removeGalleryWatermarkLogoRequest(accessToken?: string): Promise<GalleryWatermark> {
  const data = await request<{ removeGalleryWatermarkLogo: GalleryWatermark }>(
    REMOVE_GALLERY_WATERMARK_LOGO_MUTATION,
    {},
    'RemoveGalleryWatermarkLogo',
    accessToken
  );
  return data.removeGalleryWatermarkLogo;
}

// The Watermark logo (a PNG up to 5 MB) through `gallery-watermark/logo/upload-url` → S3 →
// `complete`, which makes it the mark (a LOGO). Read the template again for its signed URL.
export function uploadGalleryWatermarkLogoRequest(file: File, accessToken?: string): Promise<unknown> {
  return uploadFileDirectly({
    accessToken,
    file,
    routeUrl: `${resolveApiBaseUrl()}/gallery-watermark/logo`,
  });
}
